<template>
  <div
    class="fixed z-40 w-[min(100vw-1.5rem,24rem)] overflow-hidden rounded-2xl border border-primary/15 bg-white shadow-2xl shadow-primary/10 ring-1 ring-black/5 dark:bg-slate-900 dark:border-primary/25 dark:shadow-none dark:ring-white/5"
    :style="panelStyles"
    role="dialog"
    aria-modal="true"
    :aria-label="t('notifications_title')"
    :aria-busy="initialLoading || refreshing"
  >
    <!-- Header -->
    <div
      class="relative px-4 py-3.5 border-b border-primary/10 bg-gradient-to-br from-primary/12 via-primary/6 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="flex items-center gap-3 min-w-0">
          <span
            class="flex shrink-0 items-center justify-center w-10 h-10 rounded-xl bg-primary text-white shadow-md shadow-primary/25"
          >
            <i class="mdi mdi-bell-ring-outline text-[20px]"></i>
          </span>
          <div class="min-w-0" :dir="lang === 'ar' ? 'rtl' : 'ltr'">
            <h2 class="text-sm font-bold text-slate-900 dark:text-white text-start">
              {{ t("notifications_title") }}
            </h2>
            <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 text-start">
              <template v-if="initialLoading">
                {{ t("notifications_loading") }}
              </template>
              <template v-else-if="unreadCount > 0">
                <span class="font-semibold text-primary tabular-nums">
                  {{ unreadCount }}
                </span>
                {{ t("notifications_unread_label") }}
              </template>
              <template v-else>
                {{ t("notifications_all_read") }}
              </template>
            </p>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-1">
          <span
            v-if="refreshing"
            class="flex items-center justify-center w-8 h-8 text-primary"
            aria-hidden="true"
          >
            <i class="mdi mdi-loading mdi-spin text-[18px]"></i>
          </span>
          <button
            v-if="showMarkAll"
            type="button"
            class="inline-flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg bg-white/80 dark:bg-slate-800/80 text-primary border border-primary/20 hover:bg-primary/10 transition-colors disabled:opacity-50"
            :disabled="markingAll || refreshing"
            @click="onMarkAll"
          >
            <i class="mdi mdi-done-all text-[14px]"></i>
            {{ t("notifications_mark_all_read") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div
      class="relative max-h-[min(60vh,26rem)] min-h-[11rem] overflow-y-auto custom-scrollbar bg-white dark:bg-slate-900"
    >
      <NotificationSkeleton v-if="initialLoading" :count="4" />

      <NotificationErrorState
        v-else-if="error"
        :t="t"
        @retry="$emit('retry')"
      />

      <NotificationEmptyState
        v-else-if="showEmpty"
        compact
        :title="t('notifications_empty')"
        :subtitle="t('notifications_empty_hint')"
        :lang="lang"
        :t="t"
      />

      <div v-else class="p-2 space-y-1">
        <NotificationListItem
          v-for="item in items"
          :key="item.id"
        :notification="item"
        :lang="lang"
        :t="t"
        @select="onSelect"
        />
      </div>
    </div>

    <!-- Footer -->
    <div
      class="px-3 py-2.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-900/80"
    >
      <RouterLink
        to="/notifications"
        class="flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-xl text-primary bg-primary/8 hover:bg-primary/12 transition-colors"
        @click="$emit('close')"
      >
        {{ t("notifications_view_all") }}
        <i
          class="mdi text-[15px]"
          :class="lang === 'ar' ? 'mdi-chevron-left' : 'mdi-chevron-right'"
        ></i>
      </RouterLink>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import NotificationListItem from "./NotificationListItem.vue";
import NotificationSkeleton from "./NotificationSkeleton.vue";
import NotificationEmptyState from "./NotificationEmptyState.vue";
import NotificationErrorState from "./NotificationErrorState.vue";
import { useNotificationStore } from "@/stores/notifications";
import { isUnread } from "@/utils/notification";

const props = defineProps({
  items: { type: Array, default: () => [] },
  initialLoading: { type: Boolean, default: false },
  refreshing: { type: Boolean, default: false },
  loaded: { type: Boolean, default: false },
  error: { type: [Object, Boolean, null], default: null },
  unreadCount: { type: Number, default: 0 },
  lang: { type: String, default: "ar" },
  t: { type: Function, required: true },
  panelStyles: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["close", "retry"]);

const router = useRouter();
const store = useNotificationStore();
const markingAll = ref(false);

const showEmpty = computed(
  () => props.loaded && !props.items.length && !props.error,
);

const showMarkAll = computed(
  () =>
    props.loaded &&
    props.items.length > 0 &&
    props.items.some((n) => isUnread(n)),
);

async function onMarkAll() {
  markingAll.value = true;
  try {
    await store.markAllRead();
    emit("retry");
  } finally {
    markingAll.value = false;
  }
}

async function onSelect(notification) {
  emit("close");
  await store.openNotification(notification, router);
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
